import { Button } from '@/src/common/components/Button'
import { LoadingSpinner } from '@/src/common/components/LoadingSpinner'
import { Platform, View, Text } from 'react-native'
import { Link, usePathname, useRouter } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useLogError } from '@/src/common/hooks/useLogError'
import { ApplicationSubmissionScreen } from '@api/mesa/graphql'
import { useEffect } from 'react'
import { useDisableSwipingBackToThePreviousPage } from '@/src/common/hooks/disableSwipingBackToThePreviousPage'
import { isE2E } from '@/src/common/environment'
import { withCardGate } from '@/src/experiences/card-accounts/withCardGate'
import { useSardine } from '@/src/services/Sardine'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { GET_CARD_APPLICATION_SUBMISSION_SCREEN } from '@/src/experiences/card-application/gql/queries'
import { typography } from '@/src/common/theme'
import { cardApplicationStyles as styles } from './common'
import { Api, store } from '@/src/store'
import { cardAccountQueryArgs } from '@/src/experiences/card-accounts/hooks/useCardAccount'

export const routeForApplicationSubmissionScreen = ({
  applicationSubmissionScreen,
  navigateTo,
  handleError,
}: {
  applicationSubmissionScreen: ApplicationSubmissionScreen
  navigateTo: (path: (typeof Routes)[keyof typeof Routes]['route']) => void | Promise<void>
  handleError: () => void
}) => {
  const refetchApplicationAndAccountQueriesAndThenNavigate: typeof navigateTo = async (path) => {
    await Promise.all([
      store.dispatch(
        Api.endpoints['GET /card/onboarding/applications'].initiate(null, { forceRefetch: true }),
      ),
      store.dispatch(
        Api.endpoints['GET /card/accounts'].initiate(cardAccountQueryArgs, { forceRefetch: true }),
      ),
    ])
    await navigateTo(path)
  }

  switch (applicationSubmissionScreen) {
    case ApplicationSubmissionScreen.Submitting:
    case ApplicationSubmissionScreen.Processing:
      void refetchApplicationAndAccountQueriesAndThenNavigate(Routes.ApplicationProcessing.route)
      return
    case ApplicationSubmissionScreen.DocumentUpload:
      void refetchApplicationAndAccountQueriesAndThenNavigate(Routes.ApplicationManualReview.route)
      return
    case ApplicationSubmissionScreen.ManualReviewGeneric:
      void refetchApplicationAndAccountQueriesAndThenNavigate(Routes.ApplicationInReview.route)
      return
    case ApplicationSubmissionScreen.FrozenCredit:
      void refetchApplicationAndAccountQueriesAndThenNavigate(Routes.ApplicationCreditFrozen.route)
      return
    case ApplicationSubmissionScreen.Denied:
      void refetchApplicationAndAccountQueriesAndThenNavigate(Routes.ApplicationDenied.route)
      return
    case ApplicationSubmissionScreen.Issued:
      void refetchApplicationAndAccountQueriesAndThenNavigate(Routes.ApplicationApproved.route)
      return
    case ApplicationSubmissionScreen.NotFound:
      return
    default:
      applicationSubmissionScreen satisfies never
      handleError()
      return
  }
}

export const ApplicationProcessing = withCardGate(function ApplicationProcessing({
  refetchApplicationStatus,
}) {
  const onError = useLogError()
  const router = useRouter()
  const { updateOptions } = useSardine()
  const logError = useLogError()
  const pathname = usePathname()

  useLoggedInQuery(GET_CARD_APPLICATION_SUBMISSION_SCREEN, {
    fetchPolicy: 'no-cache',
    pollInterval: 5000,
    notifyOnNetworkStatusChange: true /** @see {@link https://github.com/apollographql/apollo-client/issues/5531#issuecomment-568235629} */,
    onCompleted({ getApplicationSubmissionScreen }) {
      routeForApplicationSubmissionScreen({
        applicationSubmissionScreen: getApplicationSubmissionScreen,
        navigateTo: (route) => {
          if (pathname === route) return
          router.replace(route)
        },
        handleError: () =>
          logError(
            new Error(`Unknown application submission screen: ${getApplicationSubmissionScreen}`),
          ),
      })
      void refetchApplicationStatus()
    },
    onError,
  })

  useEffect(() => {
    void updateOptions({ flow: 'application-processing' })
  }, [updateOptions])

  // the user might've come here from just submitting the application before,
  // so we should disable swiping back to the previous page and prevent them from re-applying somehow
  useDisableSwipingBackToThePreviousPage()

  return (
    <CardApplicationWrapper>
      <View style={styles.headerText}>
        <Text style={typography['heading/heading2']}>Your application is processing</Text>
      </View>
      <Text style={styles.instructionText}>It should only take a few seconds</Text>
      {!isE2E && (
        <View style={{ flex: 1, alignItems: 'center', marginTop: 50 }}>
          <LoadingSpinner />
        </View>
      )}
      <View style={[{ marginBottom: 0 }, Platform.OS === 'web' && { marginTop: 50 }]}>
        <Link asChild href={Routes.Main.route} replace>
          <Button title="Return Home" />
        </Link>
      </View>
    </CardApplicationWrapper>
  )
})
